// The RBP Shopping List's expanded group (RBP-D15, RBP-D17 to RBP-D23, RBP-D38), drawn in SL1.0's
// frame (`rbp_report_expanded_category.htm`) on SL2's primitives; there is no Figma frame for it.
// From the top: the group's progress bar (ordered, in cart and selected stacked against max, the
// min marker), the "Filter category" search (submitted on Enter, one read per submit), then each
// sub-group's name with its lines, or the name alone when it has none.
//
// A line shows SL1.0's columns: Supplier, Supplier PCode, Product description, Availability Info,
// Pack Size, Last Invoiced, Ordered?, Select. Every value is the server's: the state, whether the
// line can be selected, the offered alternative and the in-cart mark are never derived here. A line
// not on ShopLink has only its Select cell disabled; any other line that cannot be selected is
// disabled whole (SL1.0's two classes). The Select cell works as a product card's cart control
// (ADO-4037): at zero an Add button, which selects one; above zero the design system's
// `QuantityStepper` with a typeable value. Each change shows at once and stores the absolute
// quantity for the store (`useRbpSelection`); the quantity is the selection, so there is no check
// mark. + and Add are off where the server says the line cannot be selected, - stays on while the
// line holds a quantity, since removing (and lowering) is always allowed. A refused change shows
// the server's reason under it. Swapping Add for the stepper (and back) moves focus to the control
// that replaces the focused one, so keyboard focus is never dropped to the page.
//
// The stepper is Stepper/Default (116x40) on desktop and 136x44 (44px cells) on a tablet or phone,
// with Add drawn at the same size so the swap does not move the row; three digits fit without the
// stepper growing.
import { useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import {
  Banner,
  Button,
  Icon,
  QuantityStepper,
  StatusBadge,
  useHeaderLayout,
  type QuantityStepperCommit,
} from '../../design-system';
import {
  RBP_SELECTION_MAX_QUANTITY,
  type RbpBenefitGroupRow,
  type RbpLineAlternative,
  type RbpLineState,
  type RbpPositionLine,
  type RbpSubGroupLines,
} from '../../api/rbp.client';
import { PICKED_FRESH_LABEL } from '../../availability/pickedFresh';
import { useRbpGroupLines } from '../../rbp/useRbpGroupLines';
import { useRbpSelection } from '../../rbp/useRbpSelection';
import type { RbpPendingSelections } from '../../rbp/rbpPendingSelections';
import styles from './BenefitGroupLines.module.css';

/** SL1.0's availability texts (`get_rbp_npr.php:290-321`, `stock_and_subs.php:44-102`). */
const STATE_LABEL: Readonly<Record<RbpLineState, string>> = {
  NOT_ON_SHOPLINK: 'Not on ShopLink',
  UNAVAILABLE: 'Not available',
  PICKED_FRESH: PICKED_FRESH_LABEL,
  TEMPORARILY_OUT_OF_STOCK: 'Temp out of Stock',
  NO_STOCK_DETAILS: 'No stock details',
  IN_STOCK: 'In stock',
};

/** `YYYY-MM-DD` as SL1.0 prints it, `dd/mm/yyyy`; text only, no clock. */
function calendarDate(value: string | null): string {
  if (value === null) return '';
  const [year, month, day] = value.split('-');
  return year === undefined || month === undefined || day === undefined
    ? value
    : `${day}/${month}/${year}`;
}

/** The whole row is disabled, or only its Select cell (a line not on ShopLink). */
function disabledScope(line: RbpPositionLine): 'row' | 'select' | null {
  if (line.isSelectable) return null;
  return line.state === 'NOT_ON_SHOPLINK' ? 'select' : 'row';
}

function lineName(line: RbpPositionLine): string {
  return line.description ?? line.supplierProductCode ?? line.productCode ?? 'this line';
}

function ProgressBar({ row }: { readonly row: RbpBenefitGroupRow }) {
  const bar = row.progressBar;
  if (bar === null) return null;
  const label =
    `Ordered ${row.productsOrdered}, in cart ${row.productsInCarts}, selected ` +
    `${row.productsSelected}, min ${row.minLinesRequired}, max ${row.maxLinesRequired}`;
  const legend: ReadonlyArray<readonly [string, string, number]> = [
    ['ordered', 'ORDERED', row.productsOrdered],
    ['inCart', 'IN CART', row.productsInCarts],
    ['selected', 'SELECTED', row.productsSelected],
    ['min', 'MIN', row.minLinesRequired],
    ['max', 'MAX', row.maxLinesRequired],
  ];
  return (
    <div className={styles.progress}>
      <div role="img" aria-label={label} className={styles.bar}>
        <span
          data-segment="ordered"
          className={styles.ordered}
          style={{ width: `${bar.orderedPct}%` }}
        />
        <span
          data-segment="inCart"
          className={styles.inCart}
          style={{ width: `${bar.inCartPct}%` }}
        />
        <span
          data-segment="selected"
          className={styles.selectedSegment}
          style={{ width: `${bar.selectedPct}%` }}
        />
        <span
          data-segment="min"
          className={styles.minMarker}
          style={{ left: `${bar.minMarkerPct}%` }}
        />
        <span data-segment="max" className={styles.maxMarker} />
      </div>
      <ul className={styles.legend} aria-hidden>
        {legend.map(([key, name, value]) => (
          <li key={key} data-legend={key}>
            <span className={styles.swatch} data-legend-swatch={key} />
            <span>{name}</span> <strong>{value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Keeps only the digits of a typed quantity, capped at the line maximum. */
function sanitiseSelectionInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  return String(Math.min(Number(digits), RBP_SELECTION_MAX_QUANTITY));
}

/**
 * The "Filter category" search: one field with the search icon inside it, shown on every layout,
 * and "Clear filter" beside it once a filter is applied (it wraps under the field on a phone).
 */
function SearchForm({
  search,
  onApply,
}: {
  readonly search: string;
  readonly onApply: (search: string) => void;
}) {
  const [text, setText] = useState(search);
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(text);
  };
  const clear = () => {
    setText('');
    onApply('');
  };
  return (
    <form role="search" className={styles.search} onSubmit={onSubmit}>
      <span className={styles.searchField}>
        <Icon name="search" size={18} className={styles.searchIcon} />
        <input
          type="search"
          className={styles.searchInput}
          aria-label="Filter category"
          placeholder="Filter category"
          value={text}
          maxLength={100}
          onChange={(event) => setText(event.target.value)}
        />
      </span>
      {search === '' ? null : (
        <Button variant="secondary" className={styles.clearFilter} onClick={clear}>
          Clear filter
        </Button>
      )}
    </form>
  );
}

function Alternative({ alternative }: { readonly alternative: RbpLineAlternative }) {
  return (
    <span className={styles.alternative} data-testid="rbp-alternative">
      <Icon name="arrowsClockwise" size={14} title="RBP link available!" />
      <span className={styles.code}>{alternative.productCode}</span>
      {alternative.description === null ? null : <span>{alternative.description}</span>}
      {alternative.packSize === null ? null : <span>{alternative.packSize}</span>}
      <span data-alternative-state={alternative.state}>{STATE_LABEL[alternative.state]}</span>
    </span>
  );
}

function Description({ line }: { readonly line: RbpPositionLine }) {
  return (
    <span className={styles.description}>
      <span className={line.alternative === null ? undefined : styles.replaced}>
        {line.description ?? ''}
      </span>
      {line.isInCart ? (
        <StatusBadge tone="info" dot={false}>
          In cart
        </StatusBadge>
      ) : null}
      {line.alternative === null ? null : <Alternative alternative={line.alternative} />}
    </span>
  );
}

/** What every line's Select cell needs from the group: whose lines, and who to tell of a save. */
interface SelectContext {
  readonly benefitGroupCode: string;
  readonly onSaved: (() => void) | undefined;
  readonly pending: RbpPendingSelections | undefined;
}

function Select({
  line,
  context,
  touch,
}: {
  readonly line: RbpPositionLine;
  readonly context: SelectContext;
  /** A tablet or phone: the 44px size. */
  readonly touch: boolean;
}) {
  const selection = useRbpSelection(
    context.benefitGroupCode,
    line.productCode,
    line.selectedQuantity,
    context.onSaved === undefined ? undefined : () => context.onSaved?.(),
    context.pending,
  );
  const noCode = line.productCode === null;
  const name = lineName(line);
  const showsAdd = selection.quantity === 0;

  // Swapping Add for the stepper (or back) unmounts the focused control. When focus was in this
  // cell and fell to the page with it, it moves to the control that replaced it.
  const cell = useRef<HTMLSpanElement>(null);
  const focusInCell = useRef(false);
  const shownAdd = useRef(showsAdd);
  useLayoutEffect(() => {
    if (shownAdd.current === showsAdd) return;
    shownAdd.current = showsAdd;
    const active = document.activeElement;
    const focusFell = active === null || active === document.body;
    if (!focusInCell.current || !focusFell) return;
    cell.current
      ?.querySelector<HTMLElement>(`[data-control="${showsAdd ? 'add' : 'increase'}"]`)
      ?.focus();
  });

  const commitTyped = ({ typed }: QuantityStepperCommit) => {
    // An emptied field saves nothing (the stepper has already put the quantity back).
    if (typed === '') return;
    selection.set(Number(typed));
  };

  return (
    <span
      ref={cell}
      className={styles.selectCell}
      onFocus={() => {
        focusInCell.current = true;
      }}
      onBlur={(event) => {
        // Focus moving to another element outside the cell ends the hand-off; focus dropped with an
        // unmounted control (no related target) keeps it.
        const next = event.relatedTarget;
        if (next instanceof Node && !event.currentTarget.contains(next)) {
          focusInCell.current = false;
        }
      }}
    >
      <span className={styles.select} data-touch={touch ? 'true' : undefined}>
        {showsAdd ? (
          <Button
            variant="primary"
            className={styles.add}
            data-control="add"
            aria-label={`Add ${name} to your selection`}
            disabled={noCode || selection.clearing || !line.isSelectable}
            onClick={() => selection.set(1)}
          >
            Add
          </Button>
        ) : (
          <QuantityStepper
            className={styles.stepper}
            value={selection.quantity}
            label={`Quantity for ${name}`}
            decreaseLabel="Decrease quantity"
            increaseLabel="Increase quantity"
            onDecrease={() => selection.step(-1)}
            onIncrease={() => selection.step(1)}
            decreaseDisabled={noCode || selection.clearing}
            increaseDisabled={
              noCode ||
              selection.clearing ||
              !line.isSelectable ||
              selection.quantity >= RBP_SELECTION_MAX_QUANTITY
            }
            busy={selection.busy}
            decreaseControl="decrease"
            increaseControl="increase"
            editable={{
              inputLabel: `Quantity for ${name}`,
              sanitiseInput: sanitiseSelectionInput,
              onCommit: commitTyped,
              control: 'quantity',
            }}
          />
        )}
      </span>
      {selection.error === null ? null : (
        <span role="alert" className={styles.selectError}>
          {selection.error}
        </span>
      )}
    </span>
  );
}

const rowMarks = (line: RbpPositionLine) => ({
  'data-product-code': line.productCode ?? line.supplierProductCode ?? undefined,
  'data-on-shoplink': line.assortmentCode === null ? 'false' : 'true',
  // Styling only: rows, cells and list items do not support aria-disabled. The state text and the
  // stepper's disabled buttons tell assistive technology the line cannot be selected.
  'data-disabled': disabledScope(line) === 'row' ? 'true' : undefined,
});

function LinesTable({
  subGroup,
  context,
}: {
  readonly subGroup: RbpSubGroupLines;
  readonly context: SelectContext;
}) {
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <caption className={styles.visuallyHidden}>{`${subGroup.name} products`}</caption>
        <thead>
          <tr>
            {[
              'Supplier',
              'Supplier PCode',
              'Product description',
              'Availability Info',
              'Pack Size',
              'Last Invoiced',
              'Ordered?',
              'Select',
            ].map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subGroup.lines.map((line, index) => (
            <tr
              key={`${line.productCode ?? ''}|${line.supplierProductCode ?? ''}|${index}`}
              className={styles.line}
              {...rowMarks(line)}
            >
              <td>
                <span className={styles.supplier} title={line.supplierName ?? undefined}>
                  {line.supplierName ?? ''}
                </span>
              </td>
              <td>{line.supplierProductCode ?? ''}</td>
              <td>
                <Description line={line} />
              </td>
              <td>
                <span data-state={line.state}>{STATE_LABEL[line.state]}</span>
              </td>
              <td>{line.packSize ?? ''}</td>
              <td>{calendarDate(line.lastInvoicedOn)}</td>
              <td>{line.hasOrdered ? 'YES' : 'NO'}</td>
              <td
                data-cell="select"
                data-disabled={disabledScope(line) === 'select' ? 'true' : undefined}
              >
                <Select line={line} context={context} touch={false} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The phone and tablet card: SL1.0's mobile field order. */
function LineCards({
  subGroup,
  context,
}: {
  readonly subGroup: RbpSubGroupLines;
  readonly context: SelectContext;
}) {
  return (
    <ul className={styles.cards} aria-label={`${subGroup.name} products`}>
      {subGroup.lines.map((line, index) => (
        <li
          key={`${line.productCode ?? ''}|${line.supplierProductCode ?? ''}|${index}`}
          className={`${styles.line} ${styles.card}`}
          {...rowMarks(line)}
        >
          <Description line={line} />
          <dl className={styles.fields}>
            <div>
              <dt>Supplier</dt>
              <dd title={line.supplierName ?? undefined}>{line.supplierName ?? ''}</dd>
            </div>
            <div>
              <dt>Supplier PCode</dt>
              <dd>{line.supplierProductCode ?? ''}</dd>
            </div>
            <div>
              <dt>Pack Size</dt>
              <dd>{line.packSize ?? ''}</dd>
            </div>
            <div>
              <dt>Availability Info</dt>
              <dd>
                <span data-state={line.state}>{STATE_LABEL[line.state]}</span>
              </dd>
            </div>
            <div>
              <dt>Last Invoiced</dt>
              <dd>{calendarDate(line.lastInvoicedOn)}</dd>
            </div>
            <div>
              <dt>Ordered?</dt>
              <dd>{line.hasOrdered ? 'YES' : 'NO'}</dd>
            </div>
          </dl>
          <div
            className={styles.cardSelect}
            data-cell="select"
            data-disabled={disabledScope(line) === 'select' ? 'true' : undefined}
          >
            <Select line={line} context={context} touch />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BenefitGroupLines({
  row,
  id,
  onSelectionSaved,
  pending,
}: {
  readonly row: RbpBenefitGroupRow;
  /** The region's id, which the group's expand control names in `aria-controls`. */
  readonly id: string;
  /** Called after each stored quantity, so the page re-reads the header and the group rows. */
  readonly onSelectionSaved?: () => void;
  /** Tracks the lines' in-flight selection writes so a clear can let them settle first. */
  readonly pending?: RbpPendingSelections;
}) {
  const layout = useHeaderLayout();
  const lines = useRbpGroupLines(row.benefitGroupCode);
  const context: SelectContext = {
    benefitGroupCode: row.benefitGroupCode,
    onSaved: onSelectionSaved,
    pending,
  };
  return (
    <section id={id} className={styles.group} aria-label={`${row.description} products`}>
      <ProgressBar row={row} />
      <SearchForm search={lines.search} onApply={lines.applySearch} />
      {lines.state.status === 'loading' ? (
        <p role="status" className={styles.status}>
          Loading products…
        </p>
      ) : null}
      {lines.state.status === 'error' ? (
        <div className={styles.status}>
          <Banner type="error">We couldn&rsquo;t load this category&rsquo;s products.</Banner>
          <Button variant="secondary" onClick={lines.retry}>
            Try again
          </Button>
        </div>
      ) : null}
      {lines.state.status === 'ready'
        ? lines.state.subGroups.map((subGroup) => (
            <div key={subGroup.benefitSubGroupCode} className={styles.subGroup}>
              <h3 className={styles.subGroupName}>{subGroup.name}</h3>
              {subGroup.lines.length === 0 ? (
                <p className={styles.empty}>No products to show.</p>
              ) : layout === 'desktop' ? (
                <LinesTable subGroup={subGroup} context={context} />
              ) : (
                <LineCards subGroup={subGroup} context={context} />
              )}
            </div>
          ))
        : null}
    </section>
  );
}
